import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SearchBar = ({ 
  searchTerm, 
  onSearchChange, 
  onSearch, 
  searchHistory = [], 
  onClearHistory, 
  onRemoveHistoryItem,
  popularSearches = [],
  quickFilters = [],
  onCategorySelect,
  onSpecialFilterSelect
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [localTerm, setLocalTerm] = useState(searchTerm || '');
  const [hiddenSections, setHiddenSections] = useState({
    suggestions: false,
    history: false,
    popular: false,
    quickFilters: false
  });
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalTerm(searchTerm || '');
  }, [searchTerm]);

  const defaultPopularSearches = [
    "Modern furniture",
    "Automotive Parts",
    "Architectural elements",
    "Vehicle models"
  ];
  
  const displayPopularSearches = popularSearches.length > 0 ? popularSearches : defaultPopularSearches;

  const defaultQuickFilters = [
    { id: 'automotive', label: 'Automotive' },
    { id: 'furniture', label: 'Furniture' },
    { id: 'electronics', label: 'Electronics' }
  ];

  const displayQuickFilters = quickFilters.length > 0 ? quickFilters : defaultQuickFilters;

  const searchSuggestions = [
    "Modern office chair",
    "Futuristic car",
    "Victorian house",
    "Robot character",
    "Space station"
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (localTerm && localTerm.length > 0) {
        const filtered = searchSuggestions?.filter(suggestion =>
          suggestion?.toLowerCase()?.includes(localTerm?.toLowerCase())
        );
        setSuggestions(filtered?.slice(0, 5));
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [localTerm]);

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleSuggestionClick = (suggestion) => {
    setLocalTerm(suggestion);
    onSearch(suggestion);
    setIsDropdownOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      onSearch(localTerm);
      setIsDropdownOpen(false);
    }
  };

  const handleSearchClick = () => {
    onSearch(localTerm);
    setIsDropdownOpen(false);
  };

  const toggleSection = (section) => {
    setHiddenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className={`relative w-[80%] mx-auto transition-all duration-300 z-30`} ref={dropdownRef}>
      <div className="relative flex items-center w-full">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search Products, Components, SKU, part number, more..."
          value={localTerm}
          onChange={(e) => setLocalTerm(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className="w-full pl-12 pr-12 py-3 text-base border border-gray-400 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-primary shadow-md hover:shadow-lg transition-all duration-200 text-slate-900"
        />
        <button
          onClick={handleSearchClick}
          className="absolute left-1 top-1/2 transform -translate-y-1/2 h-9 w-9 rounded-md bg-primary text-white flex items-center justify-center"
        >
          <Icon name="Search" size={20} />
        </button>
        {localTerm && (
          <button
            onClick={() => {
              setLocalTerm('');
              onSearch('');
            }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
          >
            <Icon name="X" size={16} />
          </button>
        )}
      </div>
        
      {/* Dropdown Results */}
      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-2xl z-50 max-h-[80vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200 text-slate-900">
          
          <div className="sticky top-0 right-0 flex justify-end p-2 bg-white/95 backdrop-blur z-10 border-b border-slate-100">
             <button 
               onClick={() => setIsDropdownOpen(false)}
               className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-800 px-2 py-1 rounded-md hover:bg-slate-50 transition-colors"
             >
               <Icon name="X" size={14} /> Close
             </button>
          </div>

          {/* Search Suggestions */}
          {!hiddenSections.suggestions && suggestions?.length > 0 && (
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800">Suggestions</h3>
                <button onClick={() => toggleSection('suggestions')} className="text-xs text-slate-400 hover:text-slate-600">Hide</button>
              </div>
              <div className="space-y-1">
                {suggestions?.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="flex items-center w-full px-3 py-2 text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    <Icon name="Search" size={14} className="mr-3 flex-shrink-0 text-slate-400" />
                    <span className="truncate">{suggestion}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {hiddenSections.suggestions && suggestions?.length > 0 && (
             <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50">
               <button onClick={() => toggleSection('suggestions')} className="text-xs text-primary flex items-center gap-1">
                 <Icon name="ChevronRight" size={12} /> Show Suggestions
               </button>
             </div>
          )}

          {/* Search History */}
          {!hiddenSections.history && searchHistory?.length > 0 && (
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800">Recent Searches</h3>
                <div className="flex gap-2">
                  <button
                    onClick={onClearHistory}
                    className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Clear All
                  </button>
                  <button onClick={() => toggleSection('history')} className="text-xs text-slate-400 hover:text-slate-600">Hide</button>
                </div>
              </div>
              <div className="space-y-1">
                {searchHistory?.slice(0, 10)?.map((item, index) => (
                  <div key={index} className="group flex items-center justify-between w-full hover:bg-slate-50 rounded-md px-1">
                    <button
                      onClick={() => handleSuggestionClick(item)}
                      className="flex items-center flex-1 px-2 py-2 text-left text-sm text-slate-600 hover:text-slate-900 transition-colors"
                    >
                      <Icon name="Clock" size={14} className="mr-3 flex-shrink-0 text-slate-400" />
                      <span className="truncate">{item}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveHistoryItem(item);
                      }}
                      className="p-2 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      title="Remove from history"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hiddenSections.history && searchHistory?.length > 0 && (
             <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50">
               <button onClick={() => toggleSection('history')} className="text-xs text-primary flex items-center gap-1">
                 <Icon name="ChevronRight" size={12} /> Show Recent
               </button>
             </div>
          )}

          {/* Popular Searches */}
          {!hiddenSections.popular && (
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800">Popular Searches</h3>
                <button onClick={() => toggleSection('popular')} className="text-xs text-slate-400 hover:text-slate-600">Hide</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {displayPopularSearches?.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(search)}
                    className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full hover:bg-primary hover:text-white transition-colors"
                  >
                    {search}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {hiddenSections.popular && (
             <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50">
               <button onClick={() => toggleSection('popular')} className="text-xs text-primary flex items-center gap-1">
                 <Icon name="ChevronRight" size={12} /> Show Popular
               </button>
             </div>
          )}


          {/* Quick Filters */}
          {!hiddenSections.quickFilters && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800">Top Categories</h3>
                <button onClick={() => toggleSection('quickFilters')} className="text-xs text-slate-400 hover:text-slate-600">Hide</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {displayQuickFilters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => {
                      if (onCategorySelect) {
                        onCategorySelect(filter.id);
                        setIsDropdownOpen(false);
                      } else {
                        handleSuggestionClick(filter.label);
                      }
                    }}
                    className="flex items-center px-3 py-2 text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    <Icon name="Tag" size={14} className="mr-2 text-slate-400" />
                    {filter.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    if (onSpecialFilterSelect) {
                      onSpecialFilterSelect('new');
                      setIsDropdownOpen(false);
                    } else {
                      handleSuggestionClick('new');
                    }
                  }}
                  className="flex items-center px-3 py-2 text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <Icon name="Sparkles" size={14} className="mr-2 text-slate-400" />
                  New Arrivals
                </button>
              </div>
            </div>
          )}
          
          {hiddenSections.quickFilters && (
             <div className="px-4 py-2 bg-slate-50/50">
               <button onClick={() => toggleSection('quickFilters')} className="text-xs text-primary flex items-center gap-1">
                 <Icon name="ChevronRight" size={12} /> Show Filters
               </button>
             </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
